// ============================================================================
// The AM entry screen — everything on one screen. Run B Phase 2, 2026-08-18.
//
// Jack approved the one-screen version over the five-step wizard, so this is
// that: an AM who knows what they are doing fills it top to bottom without
// tapping Next.
//
// A record requires EXACTLY TWO things: a facility and a service. "I need a
// carpet clean at Blue Sky" is a complete, valid record. Everything else saves
// as a draft and can be filled in later. No third required field has been
// added, and none should be — every required field is a reason to force-fit,
// and force-fitting is what produced nine spellings of Strip & Wax.
//
// Every label for a division, a service, an event class or a reason is READ
// FROM THE DATABASE. None is written in this file. Those words were renamed on
// 2026-08-18 and will be renamed again. `scripts/check-vocab-literals.mjs`
// proves it against the live catalog instead of promising it.
//
// Run C Phase 1, 2026-08-19 — THE KIND PICKER IS TWO STEPS AGAIN, AND
// PROGRESSIVE. Run B rendered "Project work — bill the client" and the five
// non-project divisions as six sibling pills. That flattening deleted the only
// question on this screen that decides money — is the client being billed for
// this? — by turning it into one option among six, pre-answered, unstated.
//
// So: step one is the binary and nothing below it renders until it is answered;
// step two is the five divisions and appears only on the second branch; the
// service picker appears only once the sub-category is settled. The number of
// required FIELDS is unchanged (a facility, and a service or a reason with its
// words) — what changed is that the money question is asked out loud.
// ============================================================================
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Info, Loader2, Plus, MinusCircle } from 'lucide-react';
import { useAuth } from '../../../shared/auth';
import { Button, ErrorState, PageHeader } from '../../../shared/components';
import { SmartSelect } from '../../../components/smart-select';
import { errorMessage } from '../../../shared/errors';
import {
  CORRECTION_EVENT_CLASS,
  OTHER_SERVICE_CODE,
  PROJECT_EVENT_CLASS,
  useServiceCatalog,
} from '../hooks/use-service-catalog';
import {
  createProjectWithLines,
  type FacilityOption,
  type ProjectServiceLineInput,
} from '../hooks/use-project-data';
import {
  blankLine,
  blankChargebackLine,
  deriveAmounts,
  deriveDates,
  joinChoices,
  lineIsSaveable,
  num,
  secondOptionCaution,
  type EntryLine,
} from '../lib/entry-derive';
import { ServiceLineCard } from './service-line-card';
import { ClassifyHelperDialog, type HelperOutcome } from './classify-helper-dialog';
import { assertHelperCodesExist, UNCLASSIFIED_EVENT_CLASS } from '../lib/classify-helper';
import { ZeroPromptDialog } from './zero-prompt-dialog';
import {
  shouldUndoZeroClassification,
  type ZeroAnswer,
  type ZeroSide,
} from '../lib/zero-prompt';

const INPUT_CLASS =
  'block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text';

const PILL = 'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors';
const PILL_ON = 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink';
const PILL_OFF = 'border-tcs-border bg-tcs-bg text-tcs-text-secondary hover:border-tcs-inverse/40';

// Step one of the kind picker is a binary, so it gets buttons big enough to
// carry the consequence in words rather than a pill that only carries a noun.
// Full-width and stacked below `sm`, which is where 390px lands.
const CHOICE = 'w-full rounded-lg border px-4 py-3 text-left transition-colors';
const CHOICE_ON = 'border-tcs-inverse bg-tcs-inverse';
const CHOICE_OFF = 'border-tcs-border bg-tcs-bg hover:border-tcs-inverse/40';

const money = (n: number | null) =>
  n === null ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

let lineSeq = 0;
const nextKey = () => `line-${(lineSeq += 1)}`;

export default function AmEntryScreen() {
  const navigate = useNavigate();
  const { employee } = useAuth();
  const catalog = useServiceCatalog();

  const [facility, setFacility] = useState<FacilityOption | null>(null);
  // Run C Phase 1 — step one of the kind picker, and the only question on this
  // screen that decides money. 'project' means the client is billed for work
  // performed; 'special' means one of the five non-project divisions, which is
  // then chosen in step two.
  //
  // It starts UNANSWERED, and nothing below it renders until it is answered
  // (Ben, 2026-08-19, mid-run): the picker is progressive, so you cannot see the
  // five sub-categories before saying project-or-something-else, and you cannot
  // see the service picker before saying which sub-category. A default of
  // 'project' would have pre-answered the one question on this screen that
  // decides whether the client is billed.
  //
  // This is not a third required field. A record still requires EXACTLY two
  // things — a facility, and a service or a reason with its words. `kind` is the
  // route to one or the other, not a value of its own.
  const [kind, setKind] = useState<'project' | 'special' | null>(null);
  // '' until step two is answered. Otherwise a special division_id read from
  // v_event_classes. Only meaningful while `kind` is 'special'.
  const [specialDivision, setSpecialDivision] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [reasonNote, setReasonNote] = useState('');
  const [notes, setNotes] = useState('');
  // ⛔ RUNG 2. "I'm not sure" opens the helper, NOT a text box. `unclassified` is
  // set only when the helper itself falls through — the AM cannot reach it from
  // this screen without reading six real answers first.
  const [helperOpen, setHelperOpen] = useState(false);
  const [unclassified, setUnclassified] = useState(false);
  // ⛔ THE $0 PROMPT. Which side is being asked about, and — separately — which
  // line and side an ANSWER classified this record from. The second is what
  // makes the undo possible: we unwind our OWN inference when the amount stops
  // being zero, and never a division the AM chose deliberately on the form.
  const [zeroAsk, setZeroAsk] = useState<{ key: string; side: ZeroSide; entered: boolean } | null>(
    null,
  );
  const [zeroSetBy, setZeroSetBy] = useState<{ key: string; side: ZeroSide } | null>(null);
  // ⛔ REMOVED FROM THE FORM 2026-09-03, Ben: special_pay, awaiting_schedule and
  // due_date are no longer asked. The COLUMNS stay for history and for direct
  // database insertion — the same treatment the planned date gets — so nothing
  // written before today is lost and the door is open if any of them returns.
  // A new record simply leaves them at their default.
  const specialPay = false;
  const awaitingSchedule = false;
  const dueDate = '';
  const [lines, setLines] = useState<EntryLine[]>([blankLine(nextKey())]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ⚠️ The helper names division ids and reason codes. If either is renamed in
  // the database this shouts, rather than an AM's answer quietly mapping to a
  // code the write will refuse. Same shape as assertHourlyTierMatches.
  useEffect(() => {
    assertHelperCodesExist(
      catalog.eventClasses.map((c) => c.division_id),
      catalog.reasons.map((r) => r.reason_code),
    );
  }, [catalog.eventClasses, catalog.reasons]);

  const isSpecial = kind === 'special';
  const eventClass = unclassified
    ? UNCLASSIFIED_EVENT_CLASS
    : isSpecial
    ? (catalog.eventClasses.find((c) => c.division_id === specialDivision)?.event_class ??
      PROJECT_EVENT_CLASS)
    : PROJECT_EVENT_CLASS;
  const reasons = catalog.reasonsForDivision(isSpecial ? specialDivision || null : null);

  // The words for step one, built from the database. The five alternatives are
  // named so the AM can tell which button they want without clicking it, and
  // the caution names the one division that still bills the client — both read
  // from v_event_classes, neither typed in this file.
  const specialSummary = joinChoices(catalog.eventClasses.map((c) => c.display_name));
  const correctionLabel =
    catalog.eventClasses.find((c) => c.event_class === CORRECTION_EVENT_CLASS)?.display_name ??
    null;
  const specialCaution = secondOptionCaution(correctionLabel);

  // Progressive disclosure, in one place so the render cannot disagree with the
  // gate. Step two exists only for the non-project branch; project work has no
  // sub-category, so its service picker unlocks as soon as step one is answered.
  const kindAnswered = kind !== null;
  const subCategoryAnswered =
    kind === 'project' || unclassified || (kind === 'special' && specialDivision !== '');

  function chooseKind(next: 'project' | 'special') {
    setKind(next);
    // Leaving the non-project branch cannot leave a reason behind: reason_code
    // and reason_note are the two columns the CHECK constraints police, and a
    // stale one would either be written against event_class 'Project' or,
    // worse, silently satisfy a constraint for a division nobody chose.
    setSpecialDivision('');
    setReasonCode('');
    setUnclassified(false);
    if (next === 'project' && !specialPay) setReasonNote('');
  }

  const saveableLines = useMemo(
    () => lines.filter((l) => lineIsSaveable(l, OTHER_SERVICE_CODE)),
    [lines],
  );
  const amounts = useMemo(() => deriveAmounts(saveableLines), [saveableLines]);
  const dates = useMemo(() => deriveDates(saveableLines), [saveableLines]);

  // The whole gate, in one place.
  //  - Project work: a facility and at least one service line.
  //  - A special class: a facility, a reason code and the reason in words. Both
  //    of those are DB CHECK constraints, not opinions — a save without them is
  //    rejected by Postgres, so the screen refuses first and says why.
  //  - special_pay carries its own words, because the note column is shared.
  const blockers: string[] = [];
  if (!kindAnswered) blockers.push('what kind of record this is');
  if (!facility) blockers.push('a facility');
  if (!isSpecial && kindAnswered && saveableLines.length === 0) blockers.push('a service');
  // ⛔ An Unclassified record has no division and no reason ON PURPOSE — that is
  // what `projects_reason_code_required_for_special` already excuses. It still
  // owes the words, which `projects_reason_note_required_off_project` enforces
  // and which the helper refuses to leave without.
  if (isSpecial && !unclassified && !specialDivision) blockers.push('which kind of thing happened');
  if (isSpecial && !unclassified && specialDivision && !reasonCode) blockers.push('a reason');
  if (unclassified && !reasonNote.trim()) blockers.push('a description for Finance');
  if (isSpecial && !reasonNote.trim()) blockers.push('the reason in words');
  if (!isSpecial && specialPay && !reasonNote.trim()) blockers.push('what is different about this pay');
  const halfFilledLine = lines.some(
    (l) => l.service_code === OTHER_SERVICE_CODE && !l.scope_text.trim(),
  );
  if (halfFilledLine) blockers.push('words for the service you could not find');

  const canSubmit = blockers.length === 0 && !submitting && !!employee?.employee_id;
  const multiLine = saveableLines.length >= 2;

  function patchLine(key: string, patch: Partial<EntryLine>) {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
    // ⚠️ THE CASE THAT WOULD DO REAL DAMAGE, caught on the way past.
    //
    // The AM types 0, answers "we chose not to charge", the record becomes a
    // Concession — and then they realise it was $250 after all and type that.
    // Without this the record keeps a classification that is now simply false,
    // and we have manufactured a misclassification out of somebody CORRECTING
    // THEMSELVES. So the prompt's own inference is unwound the moment the
    // amount stops being zero; a division picked on the form is never touched.
    if (!zeroSetBy || zeroSetBy.key !== key) return;
    const nextRaw = zeroSetBy.side === 'client' ? patch.client_rate : patch.sub_rate;
    if (nextRaw === undefined) return;
    if (shouldUndoZeroClassification(nextRaw, true)) {
      setKind('project');
      setSpecialDivision('');
      setReasonCode('');
      setUnclassified(false);
      setZeroSetBy(null);
    }
  }

  /**
   * An answer to "you entered $0 — which is it?".
   *
   * ⛔ THE FIRST ANSWER ON EACH SIDE IS NOT A CLASSIFICATION. "Covered by their
   * monthly fee" and "hours are on their timecard" are the commonest true
   * meanings of a zero and they describe ORDINARY project work. They leave the
   * record exactly as it is and write the meaning into the notes, so the reason
   * survives in words for the next person who opens it — which is the one thing
   * the four flagged records in the book already had, and the only thing.
   */
  function handleZeroAnswer(answer: ZeroAnswer) {
    const asked = zeroAsk;
    setZeroAsk(null);
    if (answer.opensHelper) {
      setHelperOpen(true);
      return;
    }
    if (answer.note) {
      setNotes((prev) => (prev.trim() ? `${prev.trim()}\n${answer.note}` : (answer.note ?? '')));
    }
    if (!answer.division_id) return;
    setKind('special');
    setSpecialDivision(answer.division_id);
    setReasonCode('');
    setUnclassified(false);
    // Remember it was US who classified this, and off WHICH BOX, so a corrected
    // amount can unwind it. Only the line that raised the prompt can own that.
    if (asked) setZeroSetBy(asked);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit || !facility || !employee?.employee_id) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payloadLines: ProjectServiceLineInput[] = saveableLines.map((l, i) => ({
        service_code: l.service_code,
        scope_text: l.scope_text.trim() || null,
        scope_qty: num(l.scope_qty),
        client_rate: num(l.client_rate),
        sub_rate: num(l.sub_rate),
        assigned_partner_id: l.assigned_partner_id || null,
        service_date_planned: l.service_date_planned || null,
        service_date_actual: l.service_date_actual || null,
        line_order: i + 1,
      }));
      const result = await createProjectWithLines(
        {
          facility_id: facility.facility_id,
          account_id: facility.account_id ?? '',
          event_class: eventClass,
          reason_code: isSpecial ? reasonCode : null,
          reason_note: isSpecial || specialPay ? reasonNote.trim() || null : null,
          start_date: dates.start_date,
          completion_date: dates.completion_date,
          due_date: dueDate || null,
          bill_amount: amounts.bill_amount,
          pay_amount: amounts.pay_amount,
          // NB: assigned_partner_id is not passed here — createProjectWithLines
          // already derives it from the single line, and NULLs it for two or
          // more (TCSD-0059). Checked 2026-08-19 rather than assumed.
          special_pay: specialPay,
          awaiting_schedule: awaitingSchedule,
          notes: notes.trim() || null,
        },
        payloadLines,
        employee.employee_id,
      );
      navigate(`/project-work/${result.project_id}`);
    } catch (err) {
      setSubmitError(errorMessage(err, 'Failed to save this record.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <div className="mb-3">
        <button
          type="button"
          onClick={() => navigate('/project-work')}
          className="inline-flex items-center gap-1 text-sm text-tcs-text-secondary hover:text-tcs-text"
        >
          <ArrowLeft className="h-4 w-4" /> Back to projects
        </button>
      </div>

      <PageHeader
        title="New billable event"
        meta="A facility and a service is a complete record. Everything else can follow later."
      />

      {submitError && (
        <div className="mb-4">
          <ErrorState title="Couldn't save this record" body={submitError} />
        </div>
      )}
      {catalog.error && (
        <div className="mb-4">
          <ErrorState title="Couldn't load the vocabulary" body={catalog.error} />
        </div>
      )}

      <ClassifyHelperDialog
        open={helperOpen}
        onClose={() => setHelperOpen(false)}
        initialWords={reasonNote}
        onDone={(o: HelperOutcome) => {
          setHelperOpen(false);
          setKind('special');
          // The helper usually CLASSIFIES. Only its own fall-through leaves the
          // record for Finance, and that path carries the words with it.
          setUnclassified(o.unclassified);
          setSpecialDivision(o.division_id ?? '');
          setReasonCode(o.reason_code ?? '');
          if (o.words) setReasonNote(o.words);

          // ⭐ THE ANSWERS LAND ON THE FORM. The first pass collected two and
          // discarded the rest; Ben: *"Asking them helps Jose with the
          // classification. As it stands, it is not helpful."* Where, when and
          // who are real facts and they belong on the record, not in a modal
          // that closed.
          //
          // ⛔ FILL, NEVER OVERWRITE. Someone who typed a building and then
          // opened the helper for the classification alone must not find their
          // building replaced by a half-answered one.
          const row = o.facility_rows[0] as
            | {
                facility_id?: string;
                account_id?: string | null;
                facility_name?: string | null;
                market_region?: string | null;
              }
            | undefined;
          if (!facility && row?.facility_id) {
            setFacility({
              facility_id: row.facility_id,
              account_id: row.account_id ?? null,
              facility_name: row.facility_name ?? null,
              market_region: row.market_region ?? null,
            });
          }
          // ⚠️ A SECOND BUILDING IS NOT DROPPED SILENTLY. A project row carries
          // ONE facility_id and this run adds no column, so the extra buildings
          // go where a person will read them — the note — rather than being
          // quietly forgotten by a form that only had one slot.
          if (o.facility_ids.length > 1) {
            setReasonNote((prev) =>
              prev.includes('Also at:')
                ? prev
                : `${prev}${prev ? '\n' : ''}Also at: ${o.facility_ids.slice(1).join(', ')}`,
            );
          }
          // The date and the sub go on the FIRST line, which is the one the AM
          // is about to fill in — and only where that line is still empty.
          setLines((prev) =>
            prev.map((l, i) =>
              i === 0
                ? {
                    ...l,
                    service_date_actual: l.service_date_actual || o.date_from,
                    assigned_partner_id: l.assigned_partner_id || o.assigned_partner_id,
                  }
                : l,
            ),
          );
        }}
      />

      {/* ⛔ NOT A VALIDATION ERROR. "That's fine — which is it?" A typed $0 is a
          claim worth asking about; it is never a reason to refuse the save. */}
      <ZeroPromptDialog
        open={zeroAsk !== null}
        side={zeroAsk?.side ?? 'client'}
        entered={zeroAsk?.entered ?? true}
        onClose={() => setZeroAsk(null)}
        onAnswer={handleZeroAnswer}
      />

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* ---- Step one: the money question ---------------------------- */}
        <Section title="What kind" required>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <ChoiceButton
              selected={kind === 'project'}
              onClick={() => chooseKind('project')}
              title="Project work"
              body="Bill the client for services performed."
            />
            <ChoiceButton
              selected={kind === 'special'}
              onClick={() => chooseKind('special')}
              title="Something else"
              body={specialSummary || 'One of the non-project reasons to record money.'}
            />
          </div>
          <p className="mt-2 text-[11px] text-tcs-text-muted">
            {kind === 'special'
              ? specialCaution
              : 'This decides whether the client is billed for it. Nothing else on this screen does.'}
          </p>

          {/* ---- Step two: which one. Only after "Something else". ------ */}
          {isSpecial && (
            <div className="mt-4 border-t border-tcs-border pt-3">
              <p className="mb-1.5 text-[11px] text-tcs-text-secondary">
                Which one? <span className="text-st-danger-fg">*</span>
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {catalog.eventClasses.map((c) => (
                  <button
                    key={c.division_id}
                    type="button"
                    title={c.description ?? undefined}
                    onClick={() => {
                      setSpecialDivision(c.division_id);
                      setReasonCode('');
                      setUnclassified(false);
                    }}
                    className={`${PILL} ${specialDivision === c.division_id ? PILL_ON : PILL_OFF}`}
                  >
                    {c.display_name}
                  </button>
                ))}
                {/* ⛔ DOOR 1, AND ITS PLACEMENT IS THE POINT. Ben, 2026-09-03:
                    putting the escape last, after the five real answers, "forces
                    them to think a bit" — a top-level "I don't know" gets picked
                    for speed. Dashed and dimmer so it reads as the way out, not
                    a sixth category. It is NOT a division and never becomes one:
                    it opens the HELPER. */}
                <span aria-hidden="true" className="mx-1 h-5 w-px bg-tcs-border" />
                <button
                  type="button"
                  onClick={() => setHelperOpen(true)}
                  className={`${PILL} border-dashed ${
                    unclassified ? PILL_ON : 'border-tcs-border bg-tcs-bg text-tcs-text-secondary hover:border-tcs-text-muted'
                  }`}
                >
                  I&rsquo;m not sure — walk me through it
                </button>
              </div>
            </div>
          )}

          {/* ---- Step three: the reason, and the words. ----------------- */}
          {isSpecial && specialDivision !== '' && (
            <div className="mt-3 space-y-3">
              <div>
                <p className="mb-1.5 text-[11px] text-tcs-text-secondary">
                  Why? <span className="text-st-danger-fg">*</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {reasons.map((r) => (
                    <button
                      key={r.reason_code}
                      type="button"
                      onClick={() => setReasonCode(r.reason_code)}
                      className={`${PILL} ${reasonCode === r.reason_code ? PILL_ON : PILL_OFF}`}
                    >
                      {r.display_name}
                    </button>
                  ))}
                </div>
              </div>
              <label className="block">
                <span className="mb-1 block text-[11px] text-tcs-text-secondary">
                  Say what happened, in words <span className="text-st-danger-fg">*</span>
                </span>
                <textarea
                  value={reasonNote}
                  onChange={(e) => setReasonNote(e.target.value)}
                  rows={2}
                  placeholder="We stained the carpet during a strip and wax"
                  className={INPUT_CLASS}
                />
                <span className="mt-1 block text-[11px] text-tcs-text-muted">
                  This goes to Finance. A code on its own does not explain anything.
                </span>
              </label>
            </div>
          )}
        </Section>

        {/* ---- Facility. Progressive: only once step one is answered. --- */}
        {kindAnswered && (
        <Section title="Facility" required>
          <SmartSelect
            entity="facility"
            value={facility?.facility_id ?? null}
            onChange={(_id, row) => {
              if (!row) {
                setFacility(null);
                return;
              }
              const raw = row.raw as {
                facility_id: string;
                account_id: string | null;
                facility_name: string | null;
                market_region: string | null;
              };
              setFacility({
                facility_id: raw.facility_id,
                account_id: raw.account_id ?? null,
                facility_name: raw.facility_name ?? null,
                market_region: raw.market_region ?? null,
              });
            }}
            placeholder="Search facilities…"
          />
        </Section>
        )}

        {/* ---- Services. Progressive: the service picker stays hidden
                until the sub-category is answered, because on the
                non-project branch the sub-category is what decides whether
                a service is being recorded at all. ------------------------ */}
        {subCategoryAnswered && (
        <Section title={isSpecial ? 'What was done (optional)' : 'Service'} required={!isSpecial}>
          {isSpecial && (
            <p className="mb-2 text-[11.5px] text-tcs-text-secondary">
              A service is optional here. Record one if work actually happened — it keeps the
              facility's service history honest. {specialCaution}
            </p>
          )}
          <div className="space-y-3">
            {lines.map((l, i) => (
              <ServiceLineCard
                key={l.key}
                line={l}
                index={i}
                total={lines.length}
                facilityId={facility?.facility_id ?? null}
                // A new project has no id until it is saved, so no line of its
                // own can appear under "Has done project work here".
                projectId={null}
                otherServiceCode={OTHER_SERVICE_CODE}
                onChange={(patch) => patchLine(l.key, patch)}
                onRemove={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
                onZeroTyped={(side, entered) => setZeroAsk({ key: l.key, side, entered })}
              />
            ))}
          </div>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setLines((prev) => [...prev, blankLine(nextKey())])}
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-dashed border-tcs-border px-3 py-2 text-xs text-tcs-text-secondary hover:border-tcs-inverse/40 hover:text-tcs-text"
            >
              <Plus className="h-3.5 w-3.5" /> Add another service line
            </button>
            {/* The third money direction. Before this existed, "pay Daniel $100
                and take $50 off Alba because her crew caused it" had to be
                written into a notes field — and then filed a second time as a
                Billing Comm, because that was the only screen where the sentence
                fitted. One event, one record, both halves attached. */}
            <button
              type="button"
              onClick={() => setLines((prev) => [...prev, blankChargebackLine(nextKey())])}
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-dashed border-st-danger-border px-3 py-2 text-xs text-st-danger-fg hover:bg-st-danger-bg"
            >
              <MinusCircle className="h-3.5 w-3.5" /> Charge a sub for this
            </button>
          </div>
        </Section>
        )}

        {/* ---- What finance will see ----------------------------------- */}
        {subCategoryAnswered && (
        <Section title="What finance will see">
          <dl className="grid grid-cols-1 gap-2 text-[12.5px] sm:grid-cols-2">
            <Derived label="Bill the client">
              {amounts.bill_amount === null
                ? 'not stated — a service line has no client rate'
                : money(amounts.bill_amount)}
            </Derived>
            <Derived label="Pay the sub">
              {amounts.pay_amount === null
                ? 'not stated — a service line has no sub rate'
                : money(amounts.pay_amount)}
            </Derived>
            <Derived label="Starts">
              {dates.start_date ?? 'no planned date yet'}
            </Derived>
            <Derived label="Complete">
              {dates.completion_date ??
                (saveableLines.length > 0
                  ? 'not yet — every line needs an actual date'
                  : 'no service lines yet')}
            </Derived>
          </dl>
          <p className="mt-2 text-[11px] text-tcs-text-muted">
            Totals are only stated when every line carries a rate. A partial total would disagree
            with the line rates, and that disagreement is what decides whether the client's invoice
            is itemised or a single lump line.
          </p>

          {multiLine && (
            <p className="mt-3 flex gap-2 rounded-md bg-st-info-bg px-3 py-2 text-[11.5px] text-st-info-fg">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                This has {saveableLines.length} service lines, so finance pays each line's own
                subcontractor, one line at a time, from the project's page. Paying it from the
                project total is refused by the database, on purpose — one project amount cannot be
                split across several subs by guessing.
              </span>
            </p>
          )}

        </Section>
        )}

        {/* ---- The rest ------------------------------------------------ */}
        {subCategoryAnswered && (
        <Section title="Anything else">
          <label className="block">
            <span className="mb-1 block text-[11px] text-tcs-text-secondary">Notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Internal context, access instructions, who asked for it"
              className={INPUT_CLASS}
            />
          </label>
        </Section>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-tcs-border pt-4">
          <p className="text-[12.5px] text-tcs-text-secondary">
            {blockers.length > 0 ? (
              <>
                <Chip tone="warn">Can't save</Chip> Needs {blockers.join(', ')}. Nothing else.
              </>
            ) : (
              <>
                <Chip tone="ok">Saveable</Chip>{' '}
                {saveableLines.length === 0
                  ? 'No service line — that is allowed for this kind.'
                  : `${saveableLines.length} service line${saveableLines.length > 1 ? 's' : ''}.`}{' '}
                Everything else can follow later.
              </>
            )}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/project-work')}
              className="text-sm text-tcs-text-secondary hover:text-tcs-text"
            >
              Cancel
            </button>
            <Button type="submit" variant="primary" size="md" disabled={!canSubmit}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Section({
  title,
  required,
  children,
}: {
  title: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="border-t border-tcs-border pt-4 first:border-t-0 first:pt-0">
      <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
        {title}
        {required && <span className="ml-0.5 text-st-danger-fg">*</span>}
      </h2>
      {children}
    </section>
  );
}

/**
 * One half of the step-one binary. Carries a title and the consequence, because
 * the consequence is the whole point of asking: one of these bills the client
 * and the other usually does not.
 */
function ChoiceButton({
  selected,
  onClick,
  title,
  body,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  body: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={`${CHOICE} ${selected ? CHOICE_ON : CHOICE_OFF}`}
    >
      <span
        className={`block text-sm font-medium ${selected ? 'text-tcs-inverse-ink' : 'text-tcs-text'}`}
      >
        {title}
      </span>
      <span
        className={`mt-0.5 block text-[11.5px] ${
          selected ? 'text-tcs-inverse-ink/80' : 'text-tcs-text-secondary'
        }`}
      >
        {body}
      </span>
    </button>
  );
}

function Derived({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md bg-tcs-field px-3 py-2">
      <dt className="text-[10.5px] uppercase tracking-wide text-tcs-text-muted">{label}</dt>
      <dd className="mt-0.5 text-tcs-text">{children}</dd>
    </div>
  );
}

function Chip({ tone, children }: { tone: 'ok' | 'warn'; children: React.ReactNode }) {
  const cls = tone === 'ok' ? 'bg-st-ok-bg text-st-ok-fg' : 'bg-st-warn-bg text-st-warn-fg';
  return (
    <span className={`mr-1.5 inline-block rounded px-1.5 py-0.5 text-[10.5px] font-medium uppercase tracking-wide ${cls}`}>
      {children}
    </span>
  );
}
